import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { api, formatPKR } from '../api';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';

const COLORS = ['#8B5E3C', '#D4B483', '#5D3A23', '#B98A63', '#EEE6D8'];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getAnalytics().then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-lumora-text/50">Loading analytics...</p>;

  const revenueData = Object.entries(data.revenueByProduct || {}).map(([name, value]) => ({
    name,
    revenue: value,
  }));

  const statusData = Object.entries(data.ordersByStatus || {}).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Business performance reports" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        <StatCard label="Total Investment" value={formatPKR(data.totalInvestment)} />
        <StatCard label="Total Revenue" value={formatPKR(data.totalRevenue)} accent />
        <StatCard label="Total Profit" value={formatPKR(data.totalProfit)} accent />
        <StatCard label="Monthly Investment" value={formatPKR(data.monthlyInvestment)} />
        <StatCard label="Monthly Revenue" value={formatPKR(data.monthlyRevenue)} />
        <StatCard label="Monthly Profit" value={formatPKR(data.monthlyProfit)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <div className="card">
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Revenue by Product</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE6D8" />
              <XAxis dataKey="name" stroke="#8B5E3C" />
              <YAxis stroke="#8B5E3C" />
              <Tooltip
                contentStyle={{ background: '#FFFFFF', border: '1px solid #EEE6D8', borderRadius: '12px' }}
                formatter={(v) => formatPKR(v)}
              />
              <Bar dataKey="revenue" fill="#8B5E3C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Orders by Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {statusData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #EEE6D8', borderRadius: '12px' }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Monthly Reports</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Investment</th>
                <th>Revenue</th>
                <th>Profit</th>
              </tr>
            </thead>
            <tbody>
              {(data.monthlyReports || []).map((r) => (
                <tr key={r.month}>
                  <td>{r.month}</td>
                  <td>{formatPKR(r.investment)}</td>
                  <td>{formatPKR(r.revenue)}</td>
                  <td className={r.profit >= 0 ? 'text-green-700' : 'text-red-600'}>
                    {formatPKR(r.profit)}
                  </td>
                </tr>
              ))}
              {(!data.monthlyReports || data.monthlyReports.length === 0) && (
                <tr>
                  <td colSpan={4} className="text-center text-lumora-text/50 py-8">
                    No monthly data yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
